import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchLatestRatings, fetchStudentRatings, RATING_BATCH_SIZE } from '../services/ratingApi';

const LOADING = { status: 'loading' };
const authError = { status: 'error', message: '로그인이 필요합니다.' };
const notFoundError = { status: 'error', message: '학생 정보를 찾을 수 없습니다.' };
const errorState = (error) => ({
  status: 'error',
  message: error?.httpStatus === 403 ? '접근 권한이 없습니다.'
    : '레이팅을 불러오지 못했습니다.',
});

/** 기본 목록은 목록 조회마다 새로 계산하고, 전체 결과는 정렬 중에만 재사용한다. */
export default function useStudentRatings(students, { mode, listRequest, listReady, refreshKey = 0 }) {
  const { getAccessToken, userId, userType } = useAuth();
  const requestKey = useMemo(() => ({ userId, userType, getAccessToken, mode, listRequest, refreshKey }),
    [userId, userType, getAccessToken, mode, listRequest, refreshKey]);
  const [latest, setLatest] = useState(null);
  const [details, setDetails] = useState(null);
  const detailsRef = useRef(null);
  const batchRunRef = useRef(null);
  const studentIds = JSON.stringify([...new Set(students.map((student) => Number(student.student_id)))].sort((a, b) => a - b));

  useEffect(() => {
    if (!listRequest) return undefined;
    let active = true;
    const controller = new AbortController();
    detailsRef.current = { key: requestKey, byId: {} };
    setDetails(detailsRef.current);
    if (mode === 'page') {
      setLatest({ key: requestKey, status: 'ready', byId: {} });
      return undefined;
    }
    // 정렬 요청은 전체 학생 목록을 받는 동안 병렬로 시작한다.
    setLatest({ key: requestKey, status: 'loading' });
    fetchLatestRatings(getAccessToken, { signal: controller.signal }).then((byId) => {
      if (!active) return;
      setLatest(byId == null
        ? { key: requestKey, ...authError }
        : { key: requestKey, status: 'ready', byId });
    }).catch((error) => {
      if (active) setLatest({ key: requestKey, ...errorState(error) });
    });
    return () => { active = false; controller.abort(); };
  }, [requestKey, getAccessToken, mode, listRequest]);

  useEffect(() => {
    // 기본 목록이 확정되기 전이나 /latest가 아직 응답하지 않았을 때는 묶음 요청하지 않는다.
    if (!listReady || latest?.key !== requestKey || latest.status !== 'ready') return undefined;
    let active = true;
    const controller = new AbortController();
    const ids = new Set(JSON.parse(studentIds));
    const queue = [...ids].filter((id) => !latest.byId[id] && !detailsRef.current.byId[id]);
    const inFlight = new Set();
    const publish = (entries) => {
      if (!active || detailsRef.current.key !== requestKey) return;
      detailsRef.current = { key: requestKey, byId: { ...detailsRef.current.byId, ...entries } };
      setDetails(detailsRef.current);
    };
    // 페이지는 한 번에, 전체 정렬의 누락 학생은 최대 200명씩 순서대로 조회한다.
    const pump = () => {
      if (!active || inFlight.size > 0 || queue.length === 0) return;
      const batch = queue.splice(0, RATING_BATCH_SIZE);
      batch.forEach((id) => inFlight.add(id));
      fetchStudentRatings(batch, getAccessToken, { signal: controller.signal })
        .then((data) => {
          if (data == null) {
            publish(Object.fromEntries(batch.map((id) => [id, authError])));
            return;
          }
          publish({
            ...Object.fromEntries(Object.entries(data.byId).map(([id, entry]) => [id, { status: 'ready', ...entry }])),
            ...Object.fromEntries(data.notFound.map((id) => [id, notFoundError])),
          });
        })
        .catch((error) => publish(Object.fromEntries(batch.map((id) => [id, errorState(error)]))))
        .finally(() => { inFlight.clear(); pump(); });
    };
    const run = {
      key: requestKey,
      retry: (id) => {
        if (!active || !ids.has(id) || inFlight.has(id) || detailsRef.current.byId[id]?.status !== 'error') return;
        publish({ [id]: LOADING });
        queue.push(id);
        pump();
      },
    };
    batchRunRef.current = run;
    pump();
    return () => {
      active = false;
      controller.abort();
      if (batchRunRef.current === run) batchRunRef.current = null;
    };
  }, [studentIds, listReady, latest, requestKey, getAccessToken]);

  const retryStudent = useCallback((id) => {
    const run = batchRunRef.current;
    if (run?.key === requestKey) run.retry(Number(id));
  }, [requestKey]);

  const getRating = (id) => {
    if (!listRequest || latest?.key !== requestKey || latest.status === 'loading') return LOADING;
    if (latest.status === 'error') return latest;
    if (latest.byId[id]) return { status: 'ready', ...latest.byId[id] };
    return details?.key === requestKey ? (details.byId[id] || LOADING) : LOADING;
  };
  const status = listRequest && latest?.key === requestKey ? latest.status : 'loading';
  return { getRating, retryStudent, loading: status === 'loading', error: status === 'error' ? latest.message : null };
}
